import React, {createContext, useContext, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {DEFAULT_LANGUAGE, detectBrowserLanguage, normalizeLanguage} from './config';
import {translations} from './translations';

const LanguageContext = createContext({
    language: DEFAULT_LANGUAGE,
    t: (key) => translations[DEFAULT_LANGUAGE][key] ?? key,
});

export const LanguageProvider = ({children}) => {
    const location = useLocation();
    const browserLanguage = useMemo(() => detectBrowserLanguage(), []);
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const urlLanguage = searchParams.get('lang');
    const normalizedFromUrl = normalizeLanguage(urlLanguage);
    const language = normalizedFromUrl || browserLanguage;
    const dictionary = translations[language] ?? translations[DEFAULT_LANGUAGE];

    const translate = useMemo(() => (key, replacements) => {
        const fallbackDict = translations[DEFAULT_LANGUAGE] ?? {};
        const template = dictionary[key] ?? fallbackDict[key] ?? key;
        if (!replacements) {
            return template;
        }
        return Object.entries(replacements).reduce((acc, [replacementKey, replacementValue]) => {
            const pattern = new RegExp(`{${replacementKey}}`, 'g');
            return acc.replace(pattern, replacementValue);
        }, template);
    }, [dictionary]);

    const value = useMemo(() => ({
        language,
        t: translate,
    }), [language, translate]);

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useTranslation = () => useContext(LanguageContext);

